import { AlertTriangle, WifiOff } from 'lucide-react';
import { useEffect } from 'react';
import { session } from './lib/session';
import { useStore } from './store';
import { AdminApp } from './components/admin/AdminApp';
import { ChatDrawer } from './components/ChatDrawer';
import { Dial } from './components/Dial';
import { MixerDrawer } from './components/MixerDrawer';
import { Header } from './components/Header';
import { KeyPanel } from './components/KeyPanel';
import { Login } from './components/Login';
import { PeopleList } from './components/People';
import { ProgramDrawer } from './components/ProgramDrawer';
import { SettingsDrawer } from './components/SettingsDrawer';
import { Drawer } from './components/ui';

export function App() {
  const user = useStore((s) => s.user);
  const conn = useStore((s) => s.conn);
  const kicked = useStore((s) => s.kicked);
  const view = useStore((s) => s.view);
  const drawer = useStore((s) => s.drawer);
  const media = useStore((s) => s.media);
  const mediaError = useStore((s) => s.mediaError);
  const layout = useStore((s) => s.settings.layout);

  useEffect(() => {
    // Resume a saved session once at startup; after that, connecting is up to the login form.
    if (session.token && conn === 'idle' && !user && !session.autoConnectTried) {
      session.autoConnectTried = true;
      void session.connect();
    }
  }, [conn, user]);

  if (!user) {
    if (session.token && (conn === 'connecting' || conn === 'idle') && !useStore.getState().connError) {
      return (
        <div className="overlay">
          <div className="overlay__card">
            <div className="spinner" />
            <h2>Connecting</h2>
          </div>
        </div>
      );
    }
    return <Login />;
  }

  const close = () => useStore.setState({ drawer: 'none' });

  return (
    <div className="app">
      <Header />
      <div className="app__main">
        <div className="stage">
          {media === 'error' && (
            <div className="banner banner--error" style={{ marginBottom: 16, borderRadius: 8 }}>
              <AlertTriangle size={16} /> {mediaError}
            </div>
          )}
          {media === 'nomic' && (
            <div className="banner banner--warn" style={{ marginBottom: 16, borderRadius: 8 }}>
              <AlertTriangle size={16} /> No microphone — listen only. Allow mic access and re-open the mic from Settings.
            </div>
          )}
          {view === 'admin' ? <AdminApp /> : layout === 'dial' ? <Dial /> : <KeyPanel />}
        </div>
        <aside className="rail">
          <div className="rail__head">
            <span className="rail__title">People</span>
          </div>
          <div className="rail__body">
            <PeopleList />
          </div>
        </aside>
      </div>

      {drawer === 'people' && (
        <Drawer title="People" onClose={close}>
          <div className="drawer__body">
            <PeopleList />
          </div>
        </Drawer>
      )}
      {drawer === 'chat' && <ChatDrawer onClose={close} />}
      {drawer === 'settings' && <SettingsDrawer onClose={close} />}
      {drawer === 'program' && <ProgramDrawer onClose={close} />}
      {drawer === 'mixer' && <MixerDrawer onClose={close} />}

      {(conn === 'reconnecting' || kicked) && (
        <div className="overlay">
          <div className="overlay__card">
            {kicked ? <AlertTriangle size={40} color="var(--warning-500)" /> : <WifiOff size={40} color="var(--warning-500)" />}
            <h2>{kicked ? 'Disconnected' : 'Reconnecting'}</h2>
            <p className="muted">{kicked ?? 'Connection to the server was lost. Retrying…'}</p>
            {kicked && (
              <div className="row" style={{ justifyContent: 'center', marginTop: 16 }}>
                <button className="btn btn--primary" onClick={() => void session.connect(true)}>
                  Reconnect
                </button>
                <button className="btn btn--neutral" onClick={() => session.logout()}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
